import { NavigateFunction } from 'react-router-dom';
import { isScheduledPickupEnabled } from '@src/constants/fs-values';
import { ICreateReturnRequest, ICreateReturnShipment } from '@src/models/api';
import { IReturnShipments } from '@src/stores/returnStore';
import {
  ItemToReturn,
  OrderDetailsSessionDataV2,
  RefundValues,
  ReturnSummaryStorageData,
  ShippingInfoDict,
  SummaryShipment,
} from '../models/returns';
import { RETURN_SUMMARY_SESSION_STORAGE_KEY } from '../constants/general';
import { getWindow, roundValueAndTrim } from './general';
import { setSessionStorage } from './storage-helper';
import {
  getItemsByHrtvShipment,
  getLORPShipFromAddress,
  getSsReturnCreateShipFromAddress,
} from '.';
import { getItemToReturnItemsFromShipment } from './itemsToReturnUtils';

export const navigateToReturnSummaryPage = (navigate: NavigateFunction) => {
  if (getWindow()) {
    navigate('/myaccount/return-summary', { replace: true });
  }
};

export const persistReturnSummaryinfo = (
  returnSummaryData: ReturnSummaryStorageData,
  navigate: NavigateFunction,
) => {
  try {
    setSessionStorage(RETURN_SUMMARY_SESSION_STORAGE_KEY, returnSummaryData);
    navigateToReturnSummaryPage(navigate);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to set session storage:', error);
  }
};

export const getFinalizeReturnShipments = (
  emailId: string,
  deliveryDictionary: ShippingInfoDict,
  returnShipments: IReturnShipments[],
  selectedItems: ItemToReturn[],
  orderDetailsV2: OrderDetailsSessionDataV2,
): ICreateReturnRequest['shipments'] => {
  // One origin for every parcel leg, rather than one per leg. The helper picks
  // the ship-to-home line out of whatever list it is handed, so a leg holding
  // only store-fulfilled lines finds no shipping address and sends a blank
  // origin while a sibling leg on the same return sends a real one. Asking once
  // means the legs agree with each other, and with what the grouping call was
  // told — which is what stops the service pricing the boxes differently at
  // submit than it did at review.
  //
  // Gathered from the legs rather than by filtering items on their chosen
  // method: that method is not set on every path into this builder, and an
  // empty filter would resolve to no origin at all, quietly reproducing the
  // blank this exists to remove.
  const dropOffOrigin = getSsReturnCreateShipFromAddress(
    emailId,
    returnShipments
      ?.filter((shipment) => shipment.shipmentType === 'DROPOFF')
      .flatMap((shipment) =>
        getItemToReturnItemsFromShipment(shipment, selectedItems),
      ) || [],
    deliveryDictionary,
  );

  return (
    returnShipments?.map((shipment) => {
      const shipmentLineItems = getItemToReturnItemsFromShipment(
        shipment,
        selectedItems,
      );
      const totalWeight = shipmentLineItems.reduce(
        (acc, item) =>
          acc + (item.itemWeight || 0) * (item.returnQuantity || 0),
        0,
      );
      const isLORP = shipment.shipmentType === 'PICKUP';
      const shouldIncludeTextOptInAndPickupDate =
        isLORP && isScheduledPickupEnabled();
      const noOfBoxes = isLORP ? 1 : shipment.boxQuantity || 0;
      const shipFromAddress = isLORP
        ? getLORPShipFromAddress(
            shipment.lorpContactDetails || {},
            orderDetailsV2.fulfillmentGroups,
            selectedItems,
          )
        : dropOffOrigin;
      return {
        itemWeight: roundValueAndTrim(totalWeight),
        mVendorNumber: shipmentLineItems[0]?.mVendorNumber || '',
        noOfBoxes,
        ...(shouldIncludeTextOptInAndPickupDate
          ? {
              textOptIn: shipment.textOptIn ?? false,
              pickupDate: shipment.pickupDate ?? '',
            }
          : {}),
        lineItems: shipmentLineItems.map(
          (item): ICreateReturnShipment['lineItems'][number] => {
            const hrtvLineItem = shipment.lineItems.find(
              (lineItem) => lineItem.lineItemId === item.lineItemId,
            );
            return {
              lineItemId: item.lineItemId || '',
              poNumber: item.poNumber || '',
              sku: (item.sku as string) || '',
              returnQuantity: item.returnQuantity,
              returnReasonCode: item.returnReasonCode,
              productType: item.productType,
              rtvTokenId: hrtvLineItem?.rtvTokenId || '',
              returnDispCode: hrtvLineItem?.returnDispCode || '',
              returnPolicyId: hrtvLineItem?.returnPolicyId || 0,
              itemDescription: item.itemDescription || '',
              hazmatItem: item.hazmatItem || false,
            };
          },
        ),
        shipFromAddress,
        shipToAddress: shipment.shipToAddress,
        shipmentType: shipment.shipmentType,
      };
    }) || []
  );
};

/**
 * Build return summary data for in-store returns
 */
export const buildInStoreReturnSummaryData = (
  orderNumber: string,
  emailId: string,
  selectedItems: ItemToReturn[],
  refundValues: RefundValues | undefined,
  returnShipments: IReturnShipments[],
): ReturnSummaryStorageData => ({
  orderId: orderNumber,
  emailId,
  refundValues,
  summaryShipments: returnShipments.map((shipment) => ({
    shipmentId: shipment.shipmentId,
    shipmentType: shipment.shipmentType,
    itemsToReturn: selectedItems,
    returnByDate:
      getItemsByHrtvShipment(shipment, selectedItems)?.[0]
        .returnWindowEndDate || '',
  })),
});

/**
 * Build return summary data for LORP/multi-address returns
 */
export const buildSummaryData = (
  orderNumber: string,
  emailId: string,
  refundValues: RefundValues | undefined,
  summaryShipments: SummaryShipment[],
  selectedPickupDate: string | undefined,
  hasDuplicateReturns: boolean,
  isKeepIt = false,
): ReturnSummaryStorageData => ({
  orderId: orderNumber,
  emailId,
  refundValues,
  summaryShipments,
  selectedPickupDate,
  hasDuplicateReturns,
  isKeepIt,
});
